import { PublicClientApplication } from '@azure/msal-browser';
import { z } from 'zod';

import { IAuthClient, TokenResponse, UserContext } from '@tdp/common/auth';
import { ILogger } from '@tdp/common/telemetry';

import { MsftTenantId } from '../constants';

const TeamsAdministratorWid = '62e90394-69f5-4237-9190-012177145e10';
const GlobalAdminWid = '69091246-20e8-4a56-aa4d-066075b2a7a8';

/**
 * Get a "hint" for the user's Microsoft id (their email address if we have it).
 * this can streamline the login process by skipping the email prompt.
 */
export type LoginHintProvider = () => PromiseLike<string | undefined>;

export const MsalAuthClientAdapterConfigSchema = z.object({
  /**
   * Scopes used for the user's default login.
   * This should be the scopes for the TDP API service
   */
  // We need to convert the canonical string with comma separators into an array of strings
  loginScopes: z.preprocess(
    (v) => (typeof v === 'string' ? v.split(',') : v),
    z.array(z.string()).min(1)
  ),

  /**
   * Where AzureAd should redirect to after completion
   */
  redirectStartPage: z.string().url(),
});

type MsalAuthClientAdapterConfig = z.infer<
  typeof MsalAuthClientAdapterConfigSchema
>;

export interface MsalAuthClientAdapterDeps {
  logger: ILogger;
  application: PublicClientApplication;
  loginHintProvider: LoginHintProvider;
  config: MsalAuthClientAdapterConfig;
}

export class MsalAuthClientAdapter implements IAuthClient {
  private logger: ILogger;
  private loginHintProvider: LoginHintProvider;
  private client: PublicClientApplication;
  private config: MsalAuthClientAdapterConfig;

  constructor({
    application,
    loginHintProvider,
    config,
    logger,
  }: MsalAuthClientAdapterDeps) {
    this.client = application;
    this.loginHintProvider = loginHintProvider;
    this.logger = logger;
    this.config = config;
  }

  // TODO(nibeauli): this should probably have the ability to add additional scopes? Or is that not x-plat capable?
  // TODO: Add logout funtionality
  async login(silent?: boolean): Promise<UserContext> {
    let ctx = this.readUserContext();
    if (ctx.type != 'unauthenticated') {
      this.logger.trace('User already logged in');
      return ctx;
    }
    const { account } = (await this.client.handleRedirectPromise()) ?? {};
    if (account) {
      this.logger.trace('login complete');
      this.client.setActiveAccount(account);
    }
    const loginHint = await this.loginHintProvider();

    // attempt to read the context again after silent
    ctx = this.readUserContext();
    if (ctx.type !== 'unauthenticated') {
      this.logger.trace('User logged in via silent login');
      return ctx;
    }

    // if we aren't planning on interactively logging in the user and we' here, just return unauthenticated
    if (silent) {
      this.logger.warn(
        'Silent login requested and user is currently unauthenticated'
      );
      return ctx;
    }

    await this.client.acquireTokenRedirect({
      scopes: this.config.loginScopes,
      loginHint: loginHint,
      redirectStartPage: this.config.redirectStartPage,
    });

    throw new Error('Impossible to reach statement');
  }

  private readUserContext(): UserContext {
    const account = this.client.getActiveAccount();
    if (!account) {
      return { type: 'unauthenticated' };
    }
    const idTokenClaims = account.idTokenClaims;
    if (!idTokenClaims) {
      this.logger.trace('No token claims on current user');
      return { type: 'unauthenticated' };
    }
    const name = account.name;
    const aadId = idTokenClaims.oid;
    const preferredUserName = idTokenClaims.preferred_username;
    const wids = (idTokenClaims.wids ?? []) as string[]; // explict cast only because wids is extended part of the id token claims object

    if (!name || !aadId || !preferredUserName) {
      // Log we're missing required fields
      // only log boolean values, no the actual values
      this.logger.trace('Missing claims for current user %o', {
        aadId: !!aadId,
        preferred_username: !!preferredUserName,
        name: !!name,
      });
      return { type: 'unauthenticated' };
    }
    const isInternal = account.tenantId === MsftTenantId;

    this.logger.setUserInfo(account.tenantId, aadId);

    // we have an account, translate it into a user context object
    return {
      type: isInternal ? 'internal' : 'authenticated',
      aadId: aadId,
      isTenantAdmin: wids.some(
        (s) => s === GlobalAdminWid || s === TeamsAdministratorWid
      ),
      name: name,
      userName: account.username,
      preferredUserName,
      tenantId: account.tenantId,
    };
  }

  async getToken(scopes: string[]): Promise<TokenResponse> {
    try {
      const authenticationResult = await this.client.acquireTokenSilent({
        scopes: scopes,
      });
      //TODO(nibeauli): detect failure case and convert to unauthenticated result.
      return {
        type: 'success',
        token: authenticationResult.accessToken,
      };
    } catch (err: unknown) {
      this.logger.warn('Failed to acquire token silently', {
        error: err
      });
    }
    try {
      const authenticationResult = await this.client.acquireTokenPopup({
        scopes: scopes,
      });
      return {
        type: 'success',
        token: authenticationResult.accessToken
      };
    } catch (err: unknown) {
      this.logger.error('Failed to acquire token via popup', {
        error: err
      });
      return {
        type: 'unauthenticated',
      };
    }
  }
}

export const MsalConfigSchema = z.object({
  auth: z.object({
    clientId: z.string(),
    authority: z.optional(z.string()),
    redirectUri: z.string(),
  }),
  cache: z.optional(
    z.object({
      //TODO(nibeauli)
    })
  ),
});

export const AuthConfigSchema = z.object({
  msal: MsalConfigSchema,
  client: MsalAuthClientAdapterConfigSchema,
});

export type WebHostAuthDependencies = Omit<
  ConstructorParameters<typeof MsalAuthClientAdapter>[0],
  'application' | 'config'
> & {
  config: z.infer<typeof AuthConfigSchema>;
};

export async function composeAuth(deps: WebHostAuthDependencies) {
  const { PublicClientApplication } = await import('@azure/msal-browser');
  const msal = new PublicClientApplication(deps.config.msal);
  await msal.initialize();
  // force cast it to interface type
  const authClient = new MsalAuthClientAdapter({
    ...deps,
    application: msal,
    config: deps.config.client,
  });
  return { msal, authClient };
}
